import React, { Fragment, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import AdminSidebar from './AdminSidebar';
import AdminTopbar from './AdminTopbar';
import settingsService from '../../services/settings.service';

/**
 * Admin shell — wraps every /AdminScreen/* route.
 *
 * Layout:
 *   ┌──────────┬───────────────────────────────────┐
 *   │ Sidebar  │ Topbar (sticky)                   │
 *   │  (lg+)   ├───────────────────────────────────┤
 *   │          │  <Outlet />                       │
 *   │          │  screen content                   │
 *   └──────────┴───────────────────────────────────┘
 *
 * The Tailwind `dark` class is managed globally by <ThemeSync /> reading
 * from the Store — so this component itself doesn't need to know about
 * theme, only layout.
 */
export default function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    let active = true;

    settingsService
      .getPublicSettings()
      .then(settings => {
        if (active) setBranding(settings);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);

  return (
    <div
      data-clarity-mask='true'
      className='admin-layout flex h-screen overflow-hidden bg-background text-foreground'
    >
      {/* Desktop sidebar */}
      <div className='hidden lg:flex lg:flex-shrink-0'>
        <AdminSidebar companyName={branding?.companyName} logoURL={branding?.image?.fileLink} />
      </div>

      {/* Mobile drawer */}
      <Transition.Root show={drawerOpen} as={Fragment}>
        <Dialog as='div' className='relative z-40 lg:hidden' onClose={setDrawerOpen}>
          <Transition.Child
            as={Fragment}
            enter='transition-opacity ease-linear duration-200'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity ease-linear duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-foreground/40 backdrop-blur-sm' />
          </Transition.Child>

          <div className='fixed inset-0 flex'>
            <Transition.Child
              as={Fragment}
              enter='transition ease-out duration-200 transform'
              enterFrom='-translate-x-full'
              enterTo='translate-x-0'
              leave='transition ease-in duration-150 transform'
              leaveFrom='translate-x-0'
              leaveTo='-translate-x-full'
            >
              <Dialog.Panel className='relative flex w-full max-w-xs'>
                <AdminSidebar
                  companyName={branding?.companyName}
                  logoURL={branding?.image?.fileLink}
                  onNavigate={() => setDrawerOpen(false)}
                  onClose={() => setDrawerOpen(false)}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Right column */}
      <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
        <AdminTopbar onOpenSidebar={() => setDrawerOpen(true)} />
        <main className='flex-1 overflow-y-auto'>
          <div className='p-6'>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
